import React, { useState, useEffect } from 'react';
import { FaTimes, FaSave, FaInfoCircle, FaEdit, FaPlus } from 'react-icons/fa';
import { formatReportTypeDisplayName } from '../../models/ReportTypeDesignHelper';
import Button from '../../components/buttons/Button';
import InfoBanner from '../../components/InfoBanner/InfoBanner';
import Notification from '../../components/Notifications/Notification';
import useNotification from '../../components/Notifications/useNotifications';
import { customInstructionService } from '../../services';
import './CustomInstructionsPanel.css';
import { createLogger } from '../../services/Debug';
function CustomInstructionsPanel({ 
  isOpen, 
  onClose, 
  reportType, 
  sections,
  templateConfig 
}) {
  const log = createLogger('CustomInstructionsPanel');
  const [existingInstructions, setExistingInstructions] = useState('');
  const [newInstructions, setNewInstructions] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasExisting, setHasExisting] = useState(false);
  const [instructionId, setInstructionId] = useState(null);
  const { notification, showNotification, hideNotification } = useNotification();

  // Generate instructions from sections
  const generateInstructions = () => {
    let prompt = 'Voici la structure du rapport:\n\n';
    
    sections.filter(s => s.isVisible).forEach(section => {
      const alignment = section.alignment || 'left';
      const alignText = alignment === 'center' ? ' (centré)' : alignment === 'right' ? ' (aligné à droite)' : '';
      
      switch (section.format) {
        case 'inline':
          prompt += `${section.title}: sur une ligne${alignText}\n`;
          break;
        case 'paragraph':
          prompt += `${section.title}\nau format paragraphe${alignText}\n`;
          if (section.subtitle) {
            prompt += `Sous-titre: ${section.subtitle}\n`;
          }
          break;
        case 'list':
          prompt += `${section.title}\nau format liste${alignText}\n`;
          if (section.subtitle) {
            prompt += `Sous-titre: ${section.subtitle}\n`;
          }
          break;
        case 'table':
          prompt += `${section.title}\nau format tableau en séparant les cellules par ---${alignText}\n`;
          break;
        default:
          break;
      }
      prompt += '\n';
    });
    
    return prompt.trim();
  };

  // Load existing instructions
  useEffect(() => {
    if (!isOpen || !reportType) return;
    
    const loadInstructions = async () => {
      setIsLoading(true);
      setError('');
      
      try {
        const instructions = await customInstructionService.getCustomInstructionsForReportType(reportType);
        // Generate new instructions
        const generated = generateInstructions();
        setNewInstructions(generated);
        
        if (instructions.id) {
          const instruction = instructions.content;
          setExistingInstructions(instruction);
          setInstructionId(instructions.id);
          log.debug('instructionId', instructions.id);
          setHasExisting(true);
        } else if (instructions.content) {
          // Set existing to generated for new instructions
          setExistingInstructions(instructions.content);
          setInstructionId(null);
          setHasExisting(true);
        } else {
          setExistingInstructions('');
          setHasExisting(false);
          setInstructionId(null);
        }
      } catch (err) {
        log.error('Error loading instructions:', err);
        setError('Erreur lors du chargement des instructions');
        setHasExisting(false);
        // Still generate new instructions even if loading fails
        const generated = generateInstructions();
        setNewInstructions(generated);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadInstructions();
  }, [isOpen, reportType]);


  // Save instructions to backend
  const handleSave = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      log.debug('instructionId', instructionId);
      log.debug('reportType', reportType);

      if (instructionId) {
        // Update existing
        await customInstructionService.updateCustomInstruction(instructionId, {
          reportTypeName: reportType,
          content: existingInstructions
        });
        showNotification('Instructions personnalisées mises à jour avec succès', 'success');
      } else {
        // Create new
        await customInstructionService.createCustomInstruction({
          reportTypeName: reportType,
          content: existingInstructions
        });
        showNotification('Instructions personnalisées créées avec succès', 'success');
      }
      
      // Wait a bit for the notification to show before closing
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      log.error('Error saving instructions:', err);
      setError('Erreur lors de la sauvegarde des instructions');
      showNotification('Erreur lors de la sauvegarde des instructions', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {notification && (
        <Notification
          id={notification.id}
          message={notification.message}
          type={notification.type}
          onClose={hideNotification}
        />
      )}
      <div className="cip-overlay" onClick={onClose}>
        <div className="cip-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="cip-header">
          <h2>Instructions personnalisées</h2>
          <button className="cip-close" onClick={onClose} aria-label="Fermer">
            <FaTimes size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="cip-content">
          {error && (
            <div className="cip-error">
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="cip-loading">
              <div className="cip-spinner" />
              <p>Chargement des instructions...</p>
            </div>
          ) : (
            <>
              <div className="cip-info">
                <p>Modèle de rapport: <strong>{formatReportTypeDisplayName(reportType) || 'Non défini'}</strong></p>
              </div>

              <InfoBanner icon={FaInfoCircle}>
                Vous pouvez éditer les instructions personnalisées directement dans les zones de texte. Cliquez sur "Enregistrer" pour sauvegarder les instructions actuelles comme instructions personnalisées.<br />
                Ces instructions permetteront au modèle de suivre la structure du template créé.
              </InfoBanner>


                {/* Show side by side comparison */}
                <div className="cip-comparison">
                  <div className="cip-column">
                    <h3><FaEdit /> Instructions actuelles</h3>
                    <textarea
                      className="cip-textarea"
                      value={existingInstructions}
                      onChange={(e) => setExistingInstructions(e.target.value)}
                      placeholder="Aucunes instructions personnalisées existantes."
                    />
                  </div>
                  
                  <div className="cip-column">
                    <h3><FaPlus /> Instructions proposées</h3>
                    
                    <textarea
                      className="cip-textarea"
                      value={newInstructions}
                      onChange={(e) => setNewInstructions(e.target.value)}
                      placeholder="Nouvelles instructions générées..."
                    />
                  </div>
                </div>
              
            </>
          )}
        </div>

        {/* Footer */}
        <div className="cip-footer">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            Annuler
          </Button>
          
          <Button
            variant="primary"
            icon={FaSave}
            onClick={handleSave}
            disabled={isLoading || (!existingInstructions && !newInstructions)}
          >
            Enregistrer
          </Button>
        </div>
        </div>
      </div>
    </>
  );
}

export default CustomInstructionsPanel;